// Banner registry — single source of truth for which top-of-page
// banner renders for a given user state.
//
// BannerSlot (components/BannerSlot.js) evaluates every entry's
// condition against the current profile, sorts matches by priority
// ascending, and renders the winning banner.
//
// Priority order (locked Session 46, extended V2 Step 10e + 11a 2026-04-29,
// outage added 2026-05-09 per #121-followup):
//   0 — OutageBanner         (active vault_broadcasts channel='outage' row — highest)
//   1 — PaymentFailedBanner  (paid user in grace period)
//   2 — LapsedBanner         (trial expired, no retrial due)
//   3 — TrialCountdownBanner (active trial, daily tip + urgency)
//   4 — V2CloudOnlyBanner    (non-Chromium V2 migration — soft cloud-only)
//   5 — V2PermissionBanner   (vault folder perm decayed/blocked — reconnect)
//   6 — (reserved) LowFulBanner
//
// Condition signature: condition(profile, vaultCtx, outage) → bool.
// Profile-only banners ignore the 2nd + 3rd args (backwards-compat).
// Vault-state-driven banners (V2PermissionBanner) read vaultCtx.permissionState.
// Outage banner reads outage.activeOutage from the OutageProvider.
//
// Dismiss policies:
//   never   — no dismiss button; persistent until state resolves
//   daily   — dismissible once per calendar day (localStorage)
//   session — dismissible until tab close (sessionStorage)
//
// Mutual exclusivity in practice:
//   - payment_failed requires grace_until in the future (paid sub
//     past_due). Can't co-occur with trial banners.
//   - lapsed requires trial seat past expiry AND not retrial-eligible.
//   - trial_countdown requires trial seat + trial_ends_at in future.
//     Mutually exclusive with lapsed by definition.
//
// New banners register here by dropping an entry — no per-page
// changes needed. Enforces the Session 46 principle: "one universal
// conversion surface, everywhere, managed in one place."

import LapsedBanner from "../components/LapsedBanner";
import TrialCountdownBanner from "../components/TrialCountdownBanner";
import PaymentFailedBanner from "../components/PaymentFailedBanner";
import V2CloudOnlyBanner from "../components/V2CloudOnlyBanner";
import V2PermissionBanner from "../components/V2PermissionBanner";
import OutageBanner from "../components/OutageBanner";
import { isLapsedTrial, isRetrialEligible } from "./trial";
import { isFileSystemAccessSupported } from "./vault-local";

function isAfterNow(ts) {
  if (!ts) return false;
  try { return new Date(ts).getTime() > Date.now(); } catch { return false; }
}

export const BANNERS = [
  {
    // Outage banner — fires when an active row exists in vault_broadcasts
    // channel='outage'. Priority 0 (highest) so it outweighs payment/trial
    // banners — system status takes precedence over personal account state.
    // Non-dismissible: outage is status, not call-to-act.
    id: "outage",
    priority: 0,
    dismissPolicy: "never",
    condition: (_profile, _vaultCtx, outage) => Boolean(outage?.activeOutage),
    component: OutageBanner,
  },
  {
    id: "payment_failed",
    priority: 1,
    dismissPolicy: "never",
    condition: (profile) => isAfterNow(profile?.grace_until),
    component: PaymentFailedBanner,
  },
  {
    id: "lapsed",
    priority: 2,
    dismissPolicy: "never",
    condition: (profile) => isLapsedTrial(profile) && !isRetrialEligible(profile),
    component: LapsedBanner,
  },
  {
    id: "trial_countdown",
    priority: 3,
    dismissPolicy: "daily",
    condition: (profile) => (
      profile?.seat_type === "trial" &&
      profile?.trial_ends_at &&
      new Date(profile.trial_ends_at).getTime() > Date.now()
    ),
    component: TrialCountdownBanner,
  },
  {
    // V2 Step 10e — soft banner for non-Chromium / mobile users in the
    // V2 migration state (onboarded=true + onboarding_completed_v2=false).
    // Persistent until user clicks "Got it" → silent migration write
    // sets onboarding_completed_v2=true → condition fails → banner gone.
    // Companion to V2MigrationModal (10d) which handles the Chromium
    // hard-block path. Mutually exclusive at the condition level via
    // !isFileSystemAccessSupported() vs isFileSystemAccessSupported().
    id: "v2_cloud_only",
    priority: 4,
    dismissPolicy: "never",
    condition: (profile) => Boolean(
      profile?.onboarded === true &&
      profile?.onboarding_completed_v2 === false &&
      typeof window !== "undefined" &&
      !isFileSystemAccessSupported()
    ),
    component: V2CloudOnlyBanner,
  },
  {
    // V2 Step 11a — FS Access permission decay banner. Fires when vault.js
    // init detects an IDB-stored directoryHandle whose queryPermission
    // returns "prompt" (decayed — Chrome silent revoke after ~2 weeks
    // inactivity) or "denied" (explicit block). Banner offers [Reconnect]
    // / [Try Again] which fires handle.requestPermission() on user gesture.
    // On grant: permissionState transitions to "granted" → condition fails
    // → banner unmounts naturally.
    id: "v2_permission_decay",
    priority: 5,
    dismissPolicy: "never",
    condition: (profile, vaultCtx) => Boolean(
      profile?.onboarded === true &&
      vaultCtx &&
      (vaultCtx.permissionState === "decayed" || vaultCtx.permissionState === "blocked")
    ),
    component: V2PermissionBanner,
  },
];
